import { apiRequest } from "./api-request";

export function compactNumber(value: number): string {
  return new Intl.NumberFormat("de-DE", { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

// Markt-Dashboard: FFA-Kinomarktdaten und AGF-Streaming-Trend, beide ohne
// Titelbezug. Eigene Datei statt tobis.ts, gleiche Trennung wie im BFF
// (tobis/market.ts): zwei fachlich unabhaengige Bereiche.

async function getJson<T>(path: string): Promise<T> {
  const { response } = await apiRequest(path);
  if (!response.ok) throw new Error(`${path} → ${response.status}`);
  return (await response.json()) as T;
}

/**
 * Selbst aus den FFA-Original-PDFs extrahierte Jahresreihe 1996-2024 (siehe
 * Migrationskommentar V0054/V0055) - deckt alle neun FFA-Hauptkennzahlen ab,
 * nicht nur Besucher/Umsatz.
 */
export interface FfaYearPoint {
  year: number;
  einwohner: number | null;
  besucher: number | null;
  umsatzEur: number | null;
  eintrittspreisEur: number | null;
  kinounternehmen: number | null;
  standorte: number | null;
  spielstaetten: number | null;
  leinwaende: number | null;
  sitzplaetze: number | null;
}

export interface FfaKpiPoint {
  sourceTable: string;
  kennzahl: string;
  year: number;
  value: number | null;
}

export interface FfaRegionRow {
  region: string;
  einwohner: number | null;
  besucher: number | null;
  umsatzEur: number | null;
  eintrittspreisEur: number | null;
}

export interface FfaFormRow {
  kategorie: string;
  spielstaetten: number | null;
  tickets: number | null;
  umsatz: number | null;
  eintrittspreis: number | null;
}

export interface FfaHomevideoPoint {
  year: number;
  umsatzMioEur: number | null;
  confidence: string;
}

export interface FfaHomevideoSegmentPoint {
  year: number;
  segment: string;
  umsatzMioEur: number | null;
}

export interface FfaHomevideoKonsumentPoint {
  year: number;
  metric: string;
  value: number | null;
}

export interface FfaHomevideoFormatPoint {
  year: number;
  format: string;
  level: string;
  umsatzMioEur: number | null;
}

export interface FfaHomevideoGenrePoint {
  year: number;
  genre: string;
  genreCode: string;
  genreScheme: string;
  format: string;
  sharePct: number | null;
  confidence: string;
}

export interface FfaHomevideoKonsumklimaPoint {
  year: number;
  metric: string;
  value: number | null;
}

export interface FfaHalfyearPoint {
  year: number;
  besucherH1Mio: number | null;
  umsatzH1MioEur: number | null;
}

export interface FfaCityRow {
  stadt: string;
  einwohner: number | null;
  tickets: number | null;
  umsatzEur: number | null;
  eintrittspreisEur: number | null;
}

export interface FfaMultiplexPoint {
  year: number;
  besucher: number | null;
  umsatzEur: number | null;
  eintrittspreisEur: number | null;
}

export interface FfaMarketSharePoint {
  year: number;
  marktanteilPct: number | null;
  spielfilme: number | null;
  dokumentarfilme: number | null;
  koproduktionen: number | null;
}

/** Top 3 Titel je Jahr nach DE-Kinobesuchern, siehe FfaTopFilmPoint im BFF (tobis/market.ts). */
export interface FfaTopFilmPoint {
  year: number;
  imdbId: string;
  /** Route-Param fuer /tobis/titles/:tvmazeId. */
  tvmazeId: string;
  name: string | null;
  admissions: number | null;
  coverUrl: string | null;
}

export interface FfaSonderformRow {
  form: string;
  tickets: number | null;
  umsatzEur: number | null;
}

export interface FfaStandorteRegionRow {
  region: string;
  standorte: number | null;
  spielstaetten: number | null;
  leinwaende: number | null;
}

export interface FfaMonthPoint {
  yearMonth: string;
  besucher: number | null;
  umsatzEur: number | null;
}

export interface FfaRegionYearPoint {
  year: number;
  region: string;
  besucher: number | null;
}

export interface FfaStandorteYearPoint {
  year: number;
  region: string;
  standorte: number | null;
}

export interface FfaSonderformYearPoint {
  year: number;
  form: string;
  tickets: number | null;
}

export interface FfaYearSource {
  year: number;
  url: string;
}

export interface FfaSourceLinks {
  kinoergebnisse: FfaYearSource[];
  videoergebnisse: FfaYearSource[];
  bundeslaenderUrl: string | null;
  staedteUrl: string | null;
  kinoformUrl: string | null;
  kinogroesseUrl: string | null;
  ortsgroesseUrl: string | null;
  kinojahrMonatlich: FfaYearSource[];
  multiplexe: FfaYearSource[];
  marktanteilAlt: FfaYearSource[];
  marktanteilNeuUrl: string | null;
  standorteBundesland: FfaYearSource[];
  sonderformen: FfaYearSource[];
  bundeslaenderHistorisch: FfaYearSource[];
  staedteHistorisch: FfaYearSource[];
}

export interface FfaKinoOverview {
  kinoYearly: FfaYearPoint[];
  kpiYears: FfaKpiPoint[];
  bundeslaenderLatestYear: number | null;
  bundeslaender: FfaRegionRow[];
  kinoform: FfaFormRow[];
  kinogroesse: FfaFormRow[];
  ortsgroesse: FfaFormRow[];
  homevideoMarkt: FfaHomevideoPoint[];
  homevideoSegmente: FfaHomevideoSegmentPoint[];
  homevideoFormate: FfaHomevideoFormatPoint[];
  homevideoKonsumenten: FfaHomevideoKonsumentPoint[];
  homevideoGenres: FfaHomevideoGenrePoint[];
  homevideoKonsumklima: FfaHomevideoKonsumklimaPoint[];
  halbjahresbilanz: FfaHalfyearPoint[];
  staedteLatestYear: number | null;
  staedte: FfaCityRow[];
  bundeslaenderYearly: FfaRegionYearPoint[];
  staedteYearly: FfaRegionYearPoint[];
  kinoMonthly: FfaMonthPoint[];
  multiplexe: FfaMultiplexPoint[];
  marktanteil: FfaMarketSharePoint[];
  topFilmsByYear: FfaTopFilmPoint[];
  topGermanFilmsByYear: FfaTopFilmPoint[];
  sonderformenLatestYear: number | null;
  sonderformen: FfaSonderformRow[];
  sonderformenYearly: FfaSonderformYearPoint[];
  standorteBundeslandLatestYear: number | null;
  standorteBundesland: FfaStandorteRegionRow[];
  standorteBundeslandYearly: FfaStandorteYearPoint[];
  sources: FfaSourceLinks;
}

export async function fetchFfaKinoOverview(): Promise<FfaKinoOverview> {
  return getJson<FfaKinoOverview>("tobis-api/market/ffa-kino");
}

export interface AgfStreamingPoint {
  month: string;
  metric: string;
  audience: string;
  platform: string;
  value: number | null;
  affinityIndex: number | null;
  shareOfTvPct: number | null;
  sourceUrl: string | null;
}

export async function fetchAgfStreamingTrend(): Promise<AgfStreamingPoint[]> {
  const { points } = await getJson<{ points: AgfStreamingPoint[] }>("tobis-api/market/agf-streaming");
  return points;
}

// Klassische Fernsehquoten (agf.de/daten/tv-daten): dritter Markt-Bereich
// neben FFA-Kino/AGF-Streaming, ebenfalls ohne Titelbezug. Anders als bei den
// beiden Bereichen oben zeigt die Quelle kein Archiv - jede Zeile ist der
// zuletzt erfasste Snapshot je Zielgruppe, keine Zeitreihe.

export interface AgfTvHitlisteRow {
  audience: string;
  date: string;
  sender: string;
  titel: string;
  startzeit: string;
  dauerMin: number | null;
  sehbeteiligungMio: number | null;
  maPct: number | null;
  rang: number;
  sourceUrl: string | null;
}

export interface AgfTvMarktanteilRow {
  audience: string;
  period: string;
  sender: string;
  anteilPct: number | null;
  rang: number;
  sourceUrl: string | null;
}

export interface AgfTvOverview {
  hitliste: AgfTvHitlisteRow[];
  marktanteil: AgfTvMarktanteilRow[];
}

export async function fetchAgfTvOverview(): Promise<AgfTvOverview> {
  return getJson<AgfTvOverview>("tobis-api/market/agf-tv");
}
